// src/components/ReviewCalendar.jsx
import { useEffect, useMemo, useState } from 'react';
import { Card, Button, Badge, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function sameDay(a,b){ return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }
function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function ReviewCalendar() {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedKey, setSelectedKey] = useState(() => ymdLocal(new Date()));


  useEffect(() => {
    // Pull *your* reviews; we’ll group in the client
    const token = localStorage.getItem('token');
    setLoading(true);
    fetch('/api/reviews/user/profile', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed to load reviews');
        return r.json();
      })
      .then(data => setReviews(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message || 'Error'))
      .finally(() => setLoading(false));
  }, []);

  // Group by YYYY-MM-DD (using createdAt)
  const reviewsByDay = useMemo(() => {
    const map = new Map();
    for (const r of reviews) {
      const d = new Date(r.createdAt);
      const key = ymdLocal(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return map;
  }, [reviews]);

  // Build the 6x7 grid for the current month (with leading/trailing days)
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);

    // figure first Monday (or Sunday, choose). We'll do Sunday-start (0)
    const firstCell = new Date(first);
    firstCell.setDate(first.getDate() - first.getDay()); // Sunday-align

    const cells = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(firstCell);
      date.setDate(firstCell.getDate() + i);
      const key = ymdLocal(date);
      cells.push({
        date,
        key,
        inMonth: date.getMonth() === cursor.getMonth(),
        count: reviewsByDay.get(key)?.length || 0
      });
    }
    return cells;
  }, [cursor, reviewsByDay]);

  const dayReviews = reviewsByDay.get(selectedKey) || [];

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
        >
          ◀
        </Button>
        <h5 className="mb-0">
          {cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        </h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
        >
          ▶
        </Button>
      </div>

      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      ) : error ? (
        <Card className="mb-3"><Card.Body>Error: {error}</Card.Body></Card>
      ) : (
        <>
          {/* Weekday header */}
          <div className="calendar-grid calendar-grid-head">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d =>
              <div key={d} className="calendar-cell calendar-head">{d}</div>
            )}
          </div>

          {/* Date grid */}
          <div className="calendar-grid">
            {grid.map(cell => (
              <button
                key={cell.key}
                type="button"
                onClick={() => setSelectedKey(cell.key)}
                className={[
                  'calendar-cell',
                  cell.inMonth ? 'in-month' : 'out-month',
                  cell.key === selectedKey ? 'selected' : '',  // highlight via key
                ].join(' ')}
                aria-label={`${cell.key} (${cell.count} reviews)`}
              >
                <div className="calendar-date-num">{cell.date.getDate()}</div>
                {cell.count > 0 && (
                  <div className="calendar-dot-row">
                    {/* up to 3 dots for quick density cue */}
                    {Array.from({length: Math.min(3, cell.count)}).map((_,i) => (
                      <span className="calendar-dot" key={i} />
                    ))}
                    {cell.count > 3 && <span className="calendar-more">+{cell.count-3}</span>}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Selected day list */}
          <Card className="mt-3">
            <Card.Header>
              <strong>
                {(() => {
                  const [yy, mm, dd] = selectedKey.split('-').map(Number);
                  const localDate = new Date(yy, mm - 1, dd); // local
                  return localDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                })()}
              </strong>{' '}
              <Badge bg="secondary">{dayReviews.length}</Badge>
            </Card.Header>
            <Card.Body>
              {dayReviews.length === 0 ? (
                <div className="text-muted">No reviews this day.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {dayReviews.map(r => (
                    <div key={r._id} className="d-flex align-items-start gap-2">
                      {r.itemImage && (
                        <img
                          src={r.itemImage}
                          alt={r.itemName}
                          width={40} height={40}
                          style={{ objectFit:'cover', borderRadius:4 }}
                        />
                      )}
                      <div style={{ minWidth:0 }}>
                        <div className="d-flex align-items-center gap-2">
                          <Link to={`/${r.type}/${r.spotifyId}`} className="fw-bold text-decoration-none">
                            {r.itemName}
                          </Link>
                          <Badge bg="light" text="dark">{r.type === 'album' ? 'Album' : 'Track'}</Badge>
                        </div>
                        <div className="text-muted small">
                          {r.artists?.map(a => a.name).join(', ')}
                        </div>
                        <div className="review-snippet mt-1">{r.review}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}
